import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Tags, FileDown, ScanLine, Camera } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { ItemFilters } from "@/components/wh/ItemFilters";
import { ItemRow } from "@/components/wh/ItemRow";
import { SellRemoveDialog } from "@/components/wh/SellRemoveDialog";
import { LabelDialog } from "@/components/wh/LabelDialog";
import { PdfDialog } from "@/components/wh/PdfDialog";
import { ScanDialog } from "@/components/wh/ScanDialog";
import { BulkBar } from "@/components/wh/BulkBar";
import { useWh } from "@/lib/wh";

const EMPTY = { q: "", status: "", category: "", lot_id: "", smin: "", smax: "", cmin: "", cmax: "", dfrom: "", dto: "" };
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));

export function ItemSearch({ lotId, lots, title, onChanged }) {
  const { user } = useAuth();
  const { wh } = useWh();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [action, setAction] = useState(null);
  const [labels, setLabels] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [scan, setScan] = useState(false);
  const inputRef = useRef(null);
  const params = clean({ ...f, lot_id: lotId || f.lot_id, wh });
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    try { setItems((await api.get("/wh/items", { params: JSON.parse(key) })).data); } catch (e) { toast.error(formatApiError(e)); }
  }, [key]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/wh/categories", { params: { wh } }).then(({ data }) => setCats(data)).catch(() => {}); }, [wh]);
  const onEnter = async (e) => {
    if (e.key !== "Enter" || !f.q.trim()) return;
    e.preventDefault();
    try { const { data } = await api.get(`/wh/items/by-code/${encodeURIComponent(f.q.trim())}`, { params: { wh } }); nav(`/magazzino/articoli/${data.id}`); } catch { load(); }
  };
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const done = () => { load(); onChanged?.(); };
  const bulkDone = () => { setPicked(new Set()); done(); };
  return (
    <section className="space-y-4" data-testid="wh-item-search">
      <div className="flex gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--muted)]" />
        <Input ref={inputRef} autoFocus={!lotId} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={onEnter}
          placeholder={lotId ? "Cerca in questo lotto: descrizione, codice, marca, colore..." : "Cerca ovunque: descrizione, codice, QR, barcode, marca, colore, lotto..."}
          className="pl-9 pr-10 h-12 bg-white text-base" data-testid="wh-search-input" />
        <ScanLine className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--muted)]" aria-label="Compatibile con lettore barcode" />
      </div>
        <button type="button" onClick={() => setScan(true)} className="btn-primary h-12 px-4 shrink-0" aria-label="Scansiona con la fotocamera" data-testid="wh-scan-button"><Camera className="h-5 w-5" /><span className="hidden sm:inline">Scansiona</span></button>
      </div>
      <ItemFilters f={f} setF={setF} categories={cats} lots={lotId ? null : lots} />
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-[var(--muted)] mr-auto" data-testid="wh-result-count">{items ? `${items.length} articoli` : "Caricamento..."}</span>
        <button onClick={() => setPicked(picked.size ? new Set() : new Set((items || []).map((i) => i.id)))} className="btn-secondary h-9 px-3 text-sm" data-testid="wh-select-all">{picked.size ? "Deseleziona" : "Seleziona tutti"}</button>
        <button onClick={() => setLabels(true)} disabled={!picked.size} className="btn-secondary h-9 px-3 text-sm" data-testid="wh-labels-button"><Tags className="h-4 w-4" /> Etichette ({picked.size})</button>
        <button onClick={() => setPdf(true)} className="btn-secondary h-9 px-3 text-sm" data-testid="wh-pdf-button"><FileDown className="h-4 w-4" /> PDF / Excel</button>
      </div>
      <BulkBar ids={[...picked]} lots={(lots || []).filter((l) => l.id !== lotId)} endpoint="/wh/items/bulk" onDone={bulkDone} />
      <ul className="rounded-2xl bg-white border border-[var(--border)] px-4 divide-y divide-[var(--border)]" data-testid="wh-item-list">
        {(items || []).map((it) => <ItemRow key={it.id} it={it} selected={picked.has(it.id)} onSelect={toggle} onAction={(i, m) => setAction({ item: i, mode: m })} canWrite={can(user, "wh_write")} />)}
        {items && !items.length && <li className="py-10 text-center text-[var(--muted)]" data-testid="wh-empty">Nessun articolo trovato</li>}
      </ul>
      {action && <SellRemoveDialog item={action.item} mode={action.mode} onClose={() => setAction(null)} onDone={done} />}
      <LabelDialog open={labels} onOpenChange={setLabels} ids={[...picked]} wh={wh} />
      <PdfDialog open={pdf} onOpenChange={setPdf} lotId={lotId || f.lot_id} status={f.status} q={f.q} title={title} wh={wh} />
      <ScanDialog open={scan} onOpenChange={setScan} />
    </section>
  );
}
